import { useCallback, useEffect, useState } from 'react';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: number;
  tone: ToastTone;
  text: string;
}

const AUTO_DISMISS_MS = 2500;

let sequence = 0;

/**
 * Page-local replacement for the flashSuccess pattern: success/info messages
 * dismiss themselves, errors stay until closed. Render the result with
 * <Toast toast={toast} onDismiss={dismiss} />.
 */
export function useToast(options: { autoDismissMs?: number } = {}) {
  const autoDismissMs = options.autoDismissMs ?? AUTO_DISMISS_MS;
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const show = useCallback((tone: ToastTone, text: string) => {
    sequence += 1;
    setToast({ id: sequence, tone, text });
  }, []);

  const dismiss = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (!toast || toast.tone === 'error') return;
    const timer = setTimeout(() => setToast(null), autoDismissMs);
    return () => clearTimeout(timer);
  }, [toast, autoDismissMs]);

  return { toast, show, dismiss };
}
